/**
 * Mensagens de WhatsApp do Programa de Parceiros, enviadas à mão pela equipa
 * (botões da consola → wa.me com o texto pronto). Os mesmos textos estão em
 * docs/16-PARCEIROS-WHATSAPP.md como respostas rápidas do WhatsApp Business.
 */
import { mt } from './format';

export interface PartnerMsgCtx {
  name: string;
  /** Empresa ou gabinete: trata-se pelo nome completo, não pelo primeiro nome. */
  isCompany?: boolean;
  code: string | null;
  /** Domínio público (ex.: makila.sistemamz.com). */
  base: string;
  communityUrl?: string | null;
}

const firstWord = (name: string) => name.trim().split(/\s+/)[0] ?? name;
const urls = (c: PartnerMsgCtx) => ({
  link: `https://${c.base}/p/${c.code ?? ''}`,
  area: `https://${c.base}/parceiro/entrar`,
});
const sign = '\n\n— Equipa Makila';

export type PartnerTemplate = { id: string; label: string; hint: string; text: string };

/** Modelos para um parceiro aprovado. `extra` traz dados do momento (pagamento, empresa). */
export function partnerTemplates(c: PartnerMsgCtx, extra: { company?: string; payout?: { net: string; gross: string; withholding: string; reference: string; method: string } } = {}): PartnerTemplate[] {
  const { link, area } = urls(c);
  const first = (name: string) => (c.isCompany ? name.trim() : firstWord(name));
  const community = c.communityUrl ? `\n\n👥 Junte-se à Comunidade *Parceiros Makila* (materiais, novidades e dúvidas): ${c.communityUrl}` : '';
  const list: PartnerTemplate[] = [
    {
      id: 'welcome', label: 'Boas-vindas', hint: 'Logo depois de aprovar',
      text: `Olá, ${first(c.name)}! 👋 Bem-vindo ao *Programa de Parceiros Makila*.\n\nO seu código é *${c.code}* e o seu link é:\n${link}\n\nQuem se registar por ele tem 10% de desconto no 1.º pagamento, e você recebe 20% do que essa empresa pagar nos primeiros 12 meses.\n\nNa sua área de parceiro tem mensagens prontas, o seu QR code e um folheto para imprimir: ${area}${community}\n\nQualquer dúvida, responda a esta mensagem.${sign}`,
    },
    {
      id: 'tools', label: 'Ferramentas (dia 2–3)', hint: 'Para começar a divulgar',
      text: `Olá, ${first(c.name)}! Já espreitou as ferramentas de divulgação? 🚀\n\nNa área de parceiro (${area}) → *Ferramentas de divulgação* tem:\n• mensagens prontas para WhatsApp, email e redes, já com o seu link;\n• o seu QR code (PNG) para cartões e montras;\n• um folheto A4 com o seu código.\n\nDica: comece pelos clientes que já confiam em si — uma mensagem pessoal funciona melhor do que um grupo.${sign}`,
    },
    {
      id: 'payout-details', label: 'Pedir dados de pagamento', hint: 'Se ainda não indicou como receber',
      text: `Olá, ${first(c.name)}! Para podermos pagar as suas comissões, indique como quer receber (M-Pesa, e-Mola, mKesh ou conta bancária) na sua área de parceiro → *Receber*:\n${area}\n\nLeva menos de um minuto.${sign}`,
    },
    {
      id: 'first-company', label: 'Parabéns pela 1.ª empresa', hint: 'Quando entra a primeira indicação',
      text: `Parabéns, ${first(c.name)}! 🎉 ${extra.company ? `A *${extra.company}*` : 'A primeira empresa'} registou-se pelo seu link.\n\nQuando ela pagar a subscrição, a comissão aparece na sua área de parceiro e fica disponível 30 dias depois. Continue assim!${sign}`,
    },
    {
      id: 'reactivate', label: 'Reactivação', hint: 'Sem indicações há 30 dias',
      text: `Olá, ${first(c.name)}! Como está a correr a divulgação do Makila? 🙂\n\nSe quiser, preparamos consigo uma mensagem à medida para os seus clientes, ou fazemos uma demonstração rápida para um deles. É só dizer.\n\nO seu link continua activo: ${link}${sign}`,
    },
  ];
  if (extra.payout) {
    const p = extra.payout;
    list.push({
      id: 'paid', label: 'Pagamento feito', hint: 'Depois de registar o pagamento',
      text: `Olá, ${first(c.name)}! 💸 Pagámos as suas comissões do Programa de Parceiros.\n\n• Valor das comissões: ${mt(p.gross)} MT${Number(p.withholding) > 0 ? `\n• Retenção na fonte (IRPS): − ${mt(p.withholding)} MT` : ''}\n• *Recebido: ${mt(p.net)} MT* (${p.method})\n• Referência: ${p.reference}\n\nO detalhe está na sua área de parceiro: ${area}\nObrigado pela parceria!${sign}`,
    });
  }
  if (c.communityUrl) {
    list.push({ id: 'community', label: 'Convite para a comunidade', hint: 'Se ainda não entrou', text: `Olá, ${first(c.name)}! Criámos a Comunidade *Parceiros Makila* no WhatsApp: materiais novos, novidades e espaço para dúvidas.\n\nEntre aqui: ${c.communityUrl}${sign}` });
  }
  return list;
}

/** Para uma candidatura ainda por aprovar. */
export function applicationTemplate(name: string, isCompany = false): PartnerTemplate {
  const first = (n: string) => (isCompany ? n.trim() : firstWord(n));
  return {
    id: 'ask-info', label: 'Pedir informação', hint: 'Antes de aprovar',
    text: `Olá, ${first(name)}! Obrigado pela candidatura ao Programa de Parceiros Makila. 🙂\n\nPara a analisarmos, pode contar-nos em poucas linhas como pensa divulgar o Makila (por exemplo, quantas empresas acompanha e em que cidade)?${sign}`,
  };
}
